import { type CSSProperties, useState } from "react";

/**
 * What the app shows when the account request failed without rejecting the credential.
 *
 * The stored token is untouched and still presumed good, so this is a wait with a way out, not a
 * sign-out: the previous behavior — bounce to `/login`, or replace the app with a bare error
 * heading — made a transient failure look like a session problem and left the user nothing to do
 * about it (B-1963).
 *
 * Styled with inline `var(--color-*)` reads rather than utility classes, for the reason documented
 * at the top of `auth-bootstrap.tsx`: `@qw/auth` does not depend on `@qw/ui`, and `qw-ui.css` does
 * not `@source` this package, so Tailwind classes written here would never be generated.
 */

interface AuthUnreachableProps {
  /** Retries the request the app is gated on. Resolves either way; rejection is the caller's. */
  onRetry: () => Promise<unknown> | void;
  /** Overrides the headline for a failure that did reach the API and came back with a real error. */
  message?: string;
  hint?: string;
}

const containerStyle: CSSProperties = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: "0.75rem",
  height: "100dvh",
  justifyContent: "center",
  padding: "1.5rem",
  textAlign: "center",
};

const messageStyle: CSSProperties = {
  color: "var(--color-foreground)",
  fontSize: "0.875rem",
  margin: 0,
};

const hintStyle: CSSProperties = {
  color: "var(--color-muted-foreground)",
  fontSize: "0.8125rem",
  margin: 0,
  maxWidth: "28rem",
};

const buttonStyle: CSSProperties = {
  background: "var(--color-background)",
  border: "1px solid var(--color-input)",
  borderRadius: "0.375rem",
  color: "var(--color-foreground)",
  cursor: "pointer",
  fontSize: "0.875rem",
  padding: "0.375rem 0.875rem",
};

export const AuthUnreachable = ({
  hint = "You're still signed in — this is a connection problem, not your account.",
  message = "We couldn't reach QuoteWell.",
  onRetry,
}: AuthUnreachableProps) => {
  const [retrying, setRetrying] = useState(false);

  const retry = () => {
    setRetrying(true);
    void Promise.resolve(onRetry()).finally(() => setRetrying(false));
  };

  return (
    <div style={containerStyle}>
      <p role="status" style={messageStyle}>
        {message}
      </p>
      <p style={hintStyle}>{hint}</p>
      <button disabled={retrying} onClick={retry} style={buttonStyle} type="button">
        {retrying ? "Retrying…" : "Try again"}
      </button>
    </div>
  );
};
